import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, Plus, User } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { AuthProvider, getProfiles } from "../../pt/utils/http-service";
import { ClientData } from "../../pt/utils/interfaces";
import { useAuth } from "../../context/AuthContext";
import { formatDate } from "../../pt/utils/date-utils";
import { formatImage } from "../../pt/utils/image-utils";
import { useHistory } from "react-router-dom";

const ClientSelection = () => {
  const [clients, setClients] = useState<ClientData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const history = useHistory();
  const [isLoaded, setIsLoaded] = useState(false);
  const { isAuthenticated, getAccessTokenSilently, isLoading, loginWithPopup } =
    useAuth();
  const auth = new AuthProvider(getAccessTokenSilently);
  const { toast } = useToast();
  useEffect(() => {
    if (!isAuthenticated) return;
    getProfiles(auth).then((data) => {
      setClients(data);
      setIsLoaded(true);
    });
  }, [isAuthenticated]);
  // Mock data for now - replace with actual Supabase query later

  const filteredClients = clients.filter(
    (client) =>
      `${client.firstName} ${client.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClientSelect = (clientId: string) => {
    console.log(`Selected client ID: ${clientId}`);
    history.push(`/dashboard/${clientId}`);
  };
  const handleCreateClient = () => {

   
    history.push("/create-client");
  };

  const formatLastMeasure = (date?: Date) => {
    if (!date) return "No measurements";
    return `Last measured: ${formatDate(date)}`;
  };
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-gray-900">Trainer Login</h1>
            <p className="text-gray-600">
              Please log in to access your client dashboard
            </p>
          </div>

          <Button
            onClick={() => {
              loginWithPopup();
            }}
            className="w-full"
            size="lg"
          >
            Login
          </Button>
        </Card>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Select Client</h1>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search clients by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button onClick={handleCreateClient} className="whitespace-nowrap">
            <Plus className="w-4 h-4 mr-2" />
            New Client
          </Button>
        </div>

        <div className="grid gap-4 overflow-y-scroll max-h-[70vh]">
          {filteredClients.length === 0 ? (
            <Card className="p-8 text-center">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No clients found
              </h3>
              <p className="text-gray-600 mb-4">
                {searchQuery
                  ? "No clients match your search criteria."
                  : "You haven't created any clients yet."}
              </p>
              <Button onClick={handleCreateClient}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Client
              </Button>
            </Card>
          ) : (
            filteredClients.map((client) => (
              <Card
                key={client.ID}
                className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleClientSelect(client.ID)}
              >
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    {client.profileImage ? (
                      <img
                        src={formatImage(client.profileImage)}
                        alt={`${client.firstName} ${client.lastName}`}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {client.firstName} {client.lastName}
                    </h3>
                    <p className="text-gray-600 text-sm">{client.email}</p>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                      <span>Age: {client.age}</span>
                      <span>Height: {client.height}cm</span>
                      <span className="capitalize">{client.gender}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatLastMeasure(client.lastMeasure)}
                    </p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientSelection;
